import { Popover } from '@mantine/core'
import { Overlay } from 'ol'
import { Point } from 'ol/geom'
import { Layer } from 'ol/layer'
import VectorSource from 'ol/source/Vector'
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { LayerFilter } from './HoveredItems/types'
import { useHoveredItem } from './HoveredItems/useHoveredItems'
import { useMap } from './useMap'

type MapPopoverProps = {
  children: ReactElement
  layer: Layer
  layerFilter?: LayerFilter
  offset?: [number, number]
  width?: number
  dimensions?: [number, number]
  anchor?: 'feature' | 'pixel'
}

const overlayContainer = document.createElement('test')
document.body.appendChild(overlayContainer)

/**
 * Selected feature => children
 */
export function MapPopover(props: MapPopoverProps): ReactElement {
  const {
    children,
    layer,
    layerFilter,
    offset,
    width = 0,
    dimensions = [0, 0],
    anchor = 'feature',
  } = props

  const { map } = useMap()

  const selectedItem = useHoveredItem({
    layerFilter: layerFilter || layer.getClassName(),
    onlyIfToplayer: true,
  })

  const selectedFeature = useMemo(() => {
    if (!selectedItem) {
      return undefined
    }
    const source = layer.getSource()
    if (source instanceof VectorSource) {
      return source.getFeatureById(selectedItem.featureId)
    }
  }, [layer, selectedItem])

  const ref = useRef<HTMLDivElement | null>(null)

  const [overlayContainer] = useState(document.createElement('div'))

  const [overlay, setOverlay] = useState<Overlay>(
    new Overlay({
      autoPan: false,
      element: ref.current || undefined,
    }),
  )

  useEffect(() => {
    return () => {
      if (overlay) {
        overlay.setElement(undefined)
        overlay.setPosition(undefined)
        map.removeOverlay(overlay)
      }
    }
  }, [])

  useEffect(() => {
    if (overlay) {
      overlay.setElement(overlayContainer)
      map.addOverlay(overlay)
    }
    return () => {
      if (overlay) {
        map.removeOverlay(overlay)
      }
    }
  }, [map, overlay, overlayContainer])

  useEffect(() => {
    if (!selectedFeature) {
      overlay?.setPosition(undefined)
      return
    }
    if (typeof selectedFeature == 'object') {
      try {
        const position =
          anchor === 'feature'
            ? (selectedFeature?.getGeometry() as Point)?.getCoordinates()
            : selectedItem?.pixel
        overlay?.setPosition(position)
        if (offset) {
          overlay?.setOffset(offset)
        }
      } catch (error) {
        console.error('Popover Set', error)
      }
    }
  }, [selectedFeature, selectedItem, anchor])

  return createPortal(
    <div>
      <Popover
        position="top"
        withArrow
        shadow="md"
        opened={!!selectedFeature}
        withinPortal
      >
        <Popover.Target>
          <div
            style={{
              position: 'absolute',
              width: dimensions[0],
              height: dimensions[1],
              top: -dimensions[1],
              left: -(dimensions[0] / 2),
              pointerEvents: 'none',
            }}
          ></div>
        </Popover.Target>
        <Popover.Dropdown>
          {React.cloneElement(children, { feature: selectedFeature })}
        </Popover.Dropdown>
      </Popover>
    </div>,
    overlayContainer,
  )
}
